import React, { useState } from 'react';
import { useStableDisclosure } from '../../utils/stableDisclosure';
import styled, { css } from 'styled-components';
import { shimmerText } from './activityShimmer';
import TerminalIcon from '@mui/icons-material/Terminal';
import CodeIcon from '@mui/icons-material/Code';
import EditIcon from '@mui/icons-material/Edit';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { CodeExecutionContentItem, CodeExecTool } from '../../shared/interfaces';
import { copyToClipboard } from '../../utils/clipboard';

interface Props {
  data: CodeExecutionContentItem;
}

/**
 * Discrete inline indicator for code-execution tool calls (bash / write /
 * read / show). Mirrors the visual language of ToolActionBlock — a single
 * subtle row with icon + tool name + summary, expandable for the full
 * code/stdout/stderr — instead of the previous heavy "card" with border
 * + background + always-open body.
 */

const TOOL_ICON: Record<CodeExecTool, React.ReactNode> = {
  code_exec_bash: <TerminalIcon style={{ fontSize: 15 }} />,
  code_exec_python: <CodeIcon style={{ fontSize: 15 }} />,
  code_exec_write: <EditIcon style={{ fontSize: 15 }} />,
  code_exec_edit: <EditIcon style={{ fontSize: 15 }} />,
  code_exec_read: <DescriptionIcon style={{ fontSize: 15 }} />,
  code_exec_show: <ImageIcon style={{ fontSize: 15 }} />,
  extrair_texto_ocr: <DescriptionIcon style={{ fontSize: 15 }} />,
  analisar_visualmente: <VisibilityIcon style={{ fontSize: 15 }} />,
};

const TOOL_HEADLINE: Record<CodeExecTool, string> = {
  code_exec_bash: 'Executar comando',
  code_exec_python: 'Executar Python',
  code_exec_write: 'Escrever arquivo',
  code_exec_edit: 'Editar arquivo',
  code_exec_read: 'Ler arquivo',
  code_exec_show: 'Exibir arquivo',
  extrair_texto_ocr: 'Extrair texto (OCR)',
  analisar_visualmente: 'Analisar visualmente',
};

const Row = styled.div`
  display: flex;
  flex-direction: column;
  margin: 4px 0;
  font-family: 'Sora', 'Lexend', sans-serif;
`;

const Header = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px 4px 0;
  cursor: pointer;
  color: ${({ theme }) => theme.text_secondary || theme.font_color};
  font-size: 0.88rem;
  line-height: 1.2;
  align-self: flex-start;
  border-radius: 6px;
  user-select: none;
  max-width: 100%;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'};
  }
`;

const IconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  opacity: 0.85;
  flex-shrink: 0;
  /* Pull the icon glyph optically flush with the message text column: MUI
     SVGs carry ~3px of internal whitespace at this font size (matches
     ThinkingGroup's header alignment). */
  margin-left: -3px;
`;

const Headline = styled.span<{ $pending?: boolean }>`
  font-weight: 500;
  flex-shrink: 0;
  /* Standard activity idiom: the label shimmers while running. */
  ${({ $pending }) => ($pending ? shimmerText : css``)}
`;

const PathLabel = styled.span`
  font-family: 'Fira Code', 'Consolas', monospace;
  font-size: 0.82rem;
  opacity: 0.7;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 420px;
`;

const StatusDot = styled.div<{ $state: 'pending' | 'ok' | 'error' }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ $state }) =>
    $state === 'ok' ? '#4caf50' : $state === 'error' ? '#f44336' : '#999'};
  flex-shrink: 0;
`;

const ExitCode = styled.span<{ $error: boolean }>`
  font-size: 0.7rem;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 6px;
  background: ${({ $error }) => ($error ? '#f4433620' : '#4caf5020')};
  color: ${({ $error }) => ($error ? '#f44336' : '#4caf50')};
  font-family: 'Fira Code', 'Consolas', monospace;
  flex-shrink: 0;
`;

const ChevronWrap = styled.span`
  display: inline-flex;
  align-items: center;
  opacity: 0.6;
  flex-shrink: 0;
`;

const Body = styled.div<{ $expanded: boolean }>`
  display: ${({ $expanded }) => ($expanded ? 'block' : 'none')};
  margin: 4px 0 8px 22px;
`;

const SectionLabel = styled.div`
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.55;
  padding: 6px 0 4px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.font_color || '#666'};
`;

const CopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  border: 0;
  cursor: pointer;
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.5;
  padding: 0 4px;
  color: inherit;
  font-family: inherit;
  &:hover { opacity: 1; }
`;

const CodeArea = styled.pre<{ $error?: boolean }>`
  font-family: 'Fira Code', 'Consolas', monospace;
  font-size: 0.78rem;
  line-height: 1.5;
  background: ${({ $error, theme }) =>
    $error ? '#3a1212' : (theme as any).code_bg || '#1e1e1e'};
  color: ${({ $error, theme }) =>
    $error ? '#ffb4b4' : (theme as any).code_color || '#d4d4d4'};
  margin: 0;
  padding: 10px 12px;
  border-radius: 8px;
  overflow: auto;
  max-height: 360px;
  white-space: pre;
`;

const ErrorText = styled.div`
  margin: 4px 0 0 0;
  padding: 8px 12px;
  border-radius: 8px;
  background: #f4433620;
  color: #f44336;
  font-size: 0.78rem;
`;

const summarisePath = (item: CodeExecutionContentItem): string => {
  if (item.tool === 'code_exec_bash' || item.tool === 'code_exec_python') {
    const src = (item.tool === 'code_exec_python' ? item.args.code : item.args.command) || '';
    const cmd = src.replace(/\s+/g, ' ').trim();
    return cmd.length > 80 ? cmd.slice(0, 80) + '…' : cmd;
  }
  return item.args.path || item.path || '';
};

const inputCode = (item: CodeExecutionContentItem): string => {
  switch (item.tool) {
    case 'code_exec_bash':
      return item.args.command || '';
    case 'code_exec_python':
      return item.args.code || '';
    case 'code_exec_write':
    case 'code_exec_edit':
      return item.args.content || '';
    case 'code_exec_read':
      return item.content || '';
    case 'code_exec_show':
      return item.path || item.args.path || '';
    case 'extrair_texto_ocr':
    case 'analisar_visualmente':
      return item.content || '';
  }
};

const inputLabel = (item: CodeExecutionContentItem): string => {
  switch (item.tool) {
    case 'code_exec_bash':  return 'Comando';
    case 'code_exec_python': return 'Código';
    case 'code_exec_write': return 'Conteúdo';
    case 'code_exec_edit': return 'Alteração';
    case 'code_exec_read':  return 'Conteúdo lido';
    case 'code_exec_show':  return 'Caminho';
    case 'extrair_texto_ocr': return 'Texto extraído';
    case 'analisar_visualmente': return 'Resposta da análise';
  }
};

const CodeExecutionBlock: React.FC<Props> = ({ data }) => {
  // Default to collapsed — the discrete style is the point.
  const [expanded, setExpanded] = useState(false);
  // Recolher um corpo alto não pode deixar o navegador cortar a rolagem.
  const { disclosureRef, toggleDisclosure } = useStableDisclosure(expanded, setExpanded);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  // Stay collapsed unless the user explicitly opens it — the discrete one-line
  // chip is the default for every state, including while the code is still
  // streaming in (code_exec_delta). The streaming content keeps updating
  // underneath, so expanding mid-stream still shows it being written live.
  const showBody = expanded;

  const isError = data.success === false || (data.exit_code !== undefined && data.exit_code !== 0) ||
    // Execution error (executor unreachable / timeout / exception) carries an
    // `error` string but may have exit_code 0/undefined — still a failure.
    !!((data as any).error && String((data as any).error).trim());
  const status: 'pending' | 'ok' | 'error' = data.pending
    ? 'pending'
    : isError
    ? 'error'
    : 'ok';

  const code = inputCode(data);
  const isExec = data.tool === 'code_exec_bash' || data.tool === 'code_exec_python';
  const stdout = isExec ? (data.stdout || '') : '';
  const stderr = isExec ? (data.stderr || '') : '';
  const showExitCode =
    isExec &&
    !data.pending &&
    data.exit_code !== undefined &&
    data.exit_code !== 0;
  // stderr on a command that exited 0 is a warning stream (soffice writes
  // "failed to launch javaldx" there and still converts every file), so it gets
  // the stdout palette under "Avisos". Red is for a real failure — non-zero
  // exit or execution error — which is also what the status dot reflects.
  const stderrIsError = isError;

  const copy = (text: string, key: string) => {
    copyToClipboard(text).then((ok) => {
      if (!ok) return;
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 2000);
    });
  };
  const copyBtn = (text: string, key: string) => (
    <CopyButton onClick={(e) => { e.stopPropagation(); copy(text, key); }}>
      {copiedKey === key ? <CheckIcon style={{ fontSize: 12 }} /> : <ContentCopyIcon style={{ fontSize: 12 }} />}
      {copiedKey === key ? 'copiado' : 'copiar'}
    </CopyButton>
  );

  return (
    <Row ref={disclosureRef} data-testid="code-execution-block" data-download-exclude>
      <Header onClick={toggleDisclosure}>
        <StatusDot $state={status} data-testid="code-exec-status-dot" />
        <IconWrap>{TOOL_ICON[data.tool]}</IconWrap>
        <Headline $pending={!!data.pending} data-testid="code-exec-tool">
          {TOOL_HEADLINE[data.tool]}
        </Headline>
        <PathLabel data-testid="code-exec-path">{summarisePath(data)}</PathLabel>
        {showExitCode && (
          <ExitCode $error data-testid="code-exec-exit-code">
            exit {data.exit_code}
          </ExitCode>
        )}
        <ChevronWrap data-testid="code-exec-toggle">
          {showBody ? <ExpandLessIcon style={{ fontSize: 16 }} /> : <ExpandMoreIcon style={{ fontSize: 16 }} />}
        </ChevronWrap>
      </Header>

      <Body $expanded={showBody}>
        {code && (
          <>
            <SectionLabel>
              <span>{inputLabel(data)}</span>
              {copyBtn(code, 'input')}
            </SectionLabel>
            <CodeArea data-testid="code-exec-input">{code}</CodeArea>
          </>
        )}

        {isExec && stdout && (
          <>
            <SectionLabel>
              <span>Saída</span>
              {copyBtn(stdout, 'output')}
            </SectionLabel>
            <CodeArea data-testid="code-exec-stdout">{stdout}</CodeArea>
          </>
        )}

        {isExec && stderr && (
          <>
            <SectionLabel>
              <span>{stderrIsError ? 'Erros' : 'Avisos'}</span>
              {copyBtn(stderr, 'stderr')}
            </SectionLabel>
            <CodeArea $error={stderrIsError} data-testid="code-exec-stderr">{stderr}</CodeArea>
          </>
        )}

        {data.error && !stderr && (
          <ErrorText data-testid="code-exec-error">{data.error}</ErrorText>
        )}
      </Body>
    </Row>
  );
};

export default CodeExecutionBlock;
